import { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import RemoveOutlinedIcon from '@mui/icons-material/RemoveOutlined';

import { ErrorState } from '@/components/states';
import { useDeleteOverride, useSetOverride, useTenantUsers } from '@/api/hooks';
import { SEP_BLUE, SEP_RED } from '@/theme';
import type { AppSummary, OverrideEffect, UserAccessRow } from '@/types/api';
import { OverrideDialog, type OverrideDraft } from './OverrideDialog';

/** What the three-state control for one (user, app) currently shows. */
type UserCellMode = 'inherit' | OverrideEffect;

function modeOf(row: UserAccessRow, appKey: string): UserCellMode {
  const override = row.overrides[appKey];
  if (!override || override.isExpired) return 'inherit';
  return override.effect;
}

export function TenantUsersPanel({
  tenantKey,
  apps,
  expanded,
}: {
  tenantKey: string;
  apps: AppSummary[];
  expanded: boolean;
}) {
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<OverrideDraft | null>(null);

  const { data, isPending, error, refetch } = useTenantUsers(tenantKey, search, expanded);
  const setOverride = useSetOverride();
  const deleteOverride = useDeleteOverride();

  const visibleApps = apps.filter((app) => app.isEnabled);

  const change = (row: UserAccessRow, app: AppSummary, mode: UserCellMode) => {
    if (mode === 'inherit') {
      deleteOverride.mutate({ username: row.username, appKey: app.key });
      return;
    }
    // An explicit effect needs a reason, so it always goes through the dialog.
    setDraft({
      username: row.username,
      appKey: app.key,
      appName: app.name,
      effect: mode,
      reason: row.overrides[app.key]?.reason ?? '',
    });
  };

  if (!expanded) return null;

  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  if (isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 }, bgcolor: '#FAFAFA' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ mb: 1.5, alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <TextField
          placeholder="Benutzer suchen…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ width: { xs: '100%', sm: 280 } }}
        />
        <Typography variant="caption" color="text.secondary">
          {data.total} Benutzer · Ausnahmen überschreiben die Mandanten-Lizenz
        </Typography>
      </Stack>

      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ minWidth: 280 }}>Benutzer</TableCell>
            {visibleApps.map((app) => (
              <TableCell key={app.key} align="center" sx={{ minWidth: 130 }}>
                {app.name}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.items.map((row) => (
            <TableRow key={row.username} hover>
              <TableCell>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2">{row.username}</Typography>
                  {row.status === 'inactive' && (
                    <Chip label="inaktiv" variant="outlined" color="default" />
                  )}
                </Stack>
              </TableCell>
              {visibleApps.map((app) => {
                const mode = modeOf(row, app.key);
                const decision = row.apps[app.key];
                const override = row.overrides[app.key];
                return (
                  <TableCell key={app.key} align="center" sx={{ py: 0.5 }}>
                    <Tooltip
                      title={
                        override && !override.isExpired
                          ? `Ausnahme: ${override.reason}`
                          : decision?.allowed
                            ? 'Folgt der Mandanten-Lizenz: Zugriff'
                            : 'Folgt der Mandanten-Lizenz: kein Zugriff'
                      }
                    >
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={mode}
                        onChange={(_event, next: UserCellMode | null) => {
                          if (next !== null && next !== mode) change(row, app, next);
                        }}
                      >
                        <ToggleButton
                          value="inherit"
                          aria-label={`${row.username}: ${app.name} folgt dem Mandanten`}
                          sx={{ px: 1, py: 0.25 }}
                        >
                          <RemoveOutlinedIcon sx={{ fontSize: 16 }} />
                        </ToggleButton>
                        <ToggleButton
                          value="allow"
                          aria-label={`${row.username}: ${app.name} immer erlauben`}
                          sx={{
                            px: 1,
                            py: 0.25,
                            '&.Mui-selected': { color: SEP_BLUE, borderColor: SEP_BLUE },
                          }}
                        >
                          <CheckOutlinedIcon sx={{ fontSize: 16 }} />
                        </ToggleButton>
                        <ToggleButton
                          value="deny"
                          aria-label={`${row.username}: ${app.name} immer sperren`}
                          sx={{
                            px: 1,
                            py: 0.25,
                            '&.Mui-selected': { color: SEP_RED, borderColor: SEP_RED },
                          }}
                        >
                          <BlockOutlinedIcon sx={{ fontSize: 16 }} />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </Tooltip>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
        </Table>
      </Box>

      {data.total > data.items.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {data.items.length} von {data.total} Benutzern angezeigt — bitte suchen, um weitere
          zu finden.
        </Typography>
      )}

      <OverrideDialog
        draft={draft}
        onCancel={() => setDraft(null)}
        onConfirm={(confirmed) => {
          setOverride.mutate({
            username: confirmed.username,
            appKey: confirmed.appKey,
            effect: confirmed.effect,
            reason: confirmed.reason,
            expiresAt: confirmed.expiresAt ?? null,
          });
          setDraft(null);
        }}
      />
    </Box>
  );
}
